import {
  BanknoteArrowUp,
  CircleAlert,
  CircleX,
  Crosshair,
  Euro,
  Handshake,
  Info,
  Trash2,
} from "lucide-react";
import { type ReactNode, useState } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import type { ClaimWithRelations } from "../api/use-claim";
import { useDeleteMonetaryDemand } from "../api/use-monetary-demand-mutations";
import { MONETARY_DEMAND_TYPES_MAP } from "../lib/monetary-demand-types";
import { type AmountWithConversions, formatAmount } from "../lib/money";
import { type ClaimExpenseItem, ExpensesList } from "./expenses-list";
import { UpsertMonetaryDemandDialog } from "./upsert-monetary-demand-dialog";

export type ClaimMonetaryDemand = ClaimWithRelations["claimMonetaryDemands"][number];

interface MonetaryDemandsListProps {
  claim: ClaimWithRelations;
  monetaryDemands?: ClaimMonetaryDemand[];
  claimExpenses: ClaimExpenseItem[];
  header?: ReactNode;
}

function toAmount(amount: { currencyCode: string; amount: number } | null | undefined) {
  return amount ? formatAmount(amount as AmountWithConversions) : "";
}

/** Port of MonetaryDemands.vue — list + create/edit/delete via UpsertMonetaryDemandDialog. */
export function MonetaryDemandsList({
  claim,
  monetaryDemands,
  claimExpenses,
  header,
}: MonetaryDemandsListProps) {
  const [selectedDemand, setSelectedDemand] = useState<ClaimMonetaryDemand | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const deleteMonetaryDemand = useDeleteMonetaryDemand(claim.id);
  const canCreate = !claim.claimLegalStep?.isFinalStep;

  function openNewDemand() {
    setSelectedDemand(null);
    setDialogOpen(true);
  }

  function openExistingDemand(demand: ClaimMonetaryDemand) {
    setSelectedDemand(demand);
    setDialogOpen(true);
  }

  return (
    <div className="py-6 sm:px-6">
      {header}

      <UpsertMonetaryDemandDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        claimId={claim.id}
        existingDemand={selectedDemand}
      />

      <div className="pt-4">
        {monetaryDemands?.length ? (
          <ul className="col-span-3 w-full divide-y divide-gray-100 rounded-md border border-gray-300 bg-white shadow-sm">
            {monetaryDemands.map((monetaryDemand) => {
              const { receivedAmount, requestedAmount, agreedAmount, adversaryAmount } = monetaryDemand;
              const someReceived = monetaryDemands.some(
                (d) => d.receivedAmount && d.receivedAmount.amount > 0,
              );
              const fullyReceived = Boolean(
                receivedAmount && requestedAmount && receivedAmount.amount >= requestedAmount.amount,
              );
              const partiallyReceived = Boolean(
                receivedAmount &&
                  requestedAmount &&
                  receivedAmount.amount > 0 &&
                  receivedAmount.amount < requestedAmount.amount,
              );
              const refused = Boolean(receivedAmount && receivedAmount.amount === 0 && someReceived);

              const TypeIcon = MONETARY_DEMAND_TYPES_MAP[monetaryDemand.monetaryDemandType.type].icon;
              const deleting =
                deleteMonetaryDemand.isPending &&
                deleteMonetaryDemand.variables === monetaryDemand.monetaryDemandType.type;

              return (
                <li key={monetaryDemand.id} className="py-4 pr-5 pl-4 text-sm leading-6">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      className="flex w-0 flex-1 cursor-pointer items-center text-left"
                      onClick={() => openExistingDemand(monetaryDemand)}
                    >
                      <span className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true">
                        <TypeIcon size={20} />
                      </span>
                      <div className="ml-4 flex min-w-0 flex-1 items-center justify-between">
                        <span className="font-medium text-ellipsis">
                          {monetaryDemand.monetaryDemandType.displayText}
                        </span>

                        {fullyReceived && receivedAmount ? (
                          <span className="ml-3 inline-flex items-center gap-x-1.5 rounded-md bg-green-50 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-green-700 ring-1 ring-green-600/20 ring-inset">
                            <BanknoteArrowUp size={16} />
                            Obtenu : <strong>{toAmount(receivedAmount)}</strong>
                          </span>
                        ) : partiallyReceived && receivedAmount ? (
                          <span className="ml-3 inline-flex items-center gap-x-1.5 rounded-md bg-green-50 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-amber-700 ring-1 ring-green-600/20 ring-inset">
                            <BanknoteArrowUp size={16} />
                            Obtenu : <strong>{toAmount(receivedAmount)}</strong> / {toAmount(requestedAmount)}
                          </span>
                        ) : agreedAmount && requestedAmount ? (
                          <span className="ml-3 inline-flex items-center gap-x-1.5 rounded-md bg-purple-50 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-purple-700 ring-1 ring-purple-600/20 ring-inset">
                            <Handshake size={16} />
                            Accordé : <strong>{toAmount(agreedAmount)}</strong> / {toAmount(requestedAmount)}
                          </span>
                        ) : refused ? (
                          <span className="ml-3 inline-flex items-center gap-x-1.5 rounded-md bg-yellow-50 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-yellow-700 ring-1 ring-yellow-600/20 ring-inset">
                            <CircleX size={16} />
                            Refusé
                          </span>
                        ) : requestedAmount ? (
                          <span className="ml-3 inline-flex items-center gap-x-1.5 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-slate-700 ring-1 ring-slate-600/20 ring-inset">
                            <Crosshair size={16} />
                            Montant demandé : {toAmount(requestedAmount)}
                          </span>
                        ) : null}
                      </div>
                    </button>
                    {/* Generated demands (EC261…) can't be deleted — MonetaryDemands.vue parity. */}
                    {!monetaryDemand.monetaryDemandType.isGenerated ? (
                      <button
                        type="button"
                        className="ml-4 shrink-0 cursor-pointer rounded-md font-medium text-gray-400 hover:text-red-500 disabled:opacity-50"
                        disabled={deleting}
                        aria-label="Supprimer la demande"
                        onClick={() => deleteMonetaryDemand.mutate(monetaryDemand.monetaryDemandType.type)}
                      >
                        {deleting ? <Spinner className="h-5 w-5" /> : <Trash2 className="h-5 w-5" />}
                      </button>
                    ) : null}
                  </div>

                  <div className="space-y-1 py-2 pl-9 text-xs text-gray-500">
                    {requestedAmount && receivedAmount && (fullyReceived || refused) ? (
                      <div className="flex items-center gap-1.5">
                        <Euro size={12} className="text-gray-400" />
                        <span>Demandé : {toAmount(requestedAmount)}</span>
                      </div>
                    ) : null}
                    {agreedAmount && receivedAmount ? (
                      <div className="flex items-center gap-1.5">
                        <Handshake size={12} className="text-gray-400" />
                        <span>Accordé : {toAmount(agreedAmount)}</span>
                      </div>
                    ) : null}
                    {adversaryAmount ? (
                      <div className="flex items-center gap-1.5">
                        <CircleAlert size={12} className="text-gray-400" />
                        <span>Proposé par l'adversaire : {toAmount(adversaryAmount)}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="-mt-4">
                    <ExpensesList
                      claim={claim}
                      claimExpenses={claimExpenses.filter(
                        ({ expense }) => expense.claimMonetaryDemandId === monetaryDemand.id,
                      )}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="col-span-3 mb-5 w-full divide-y divide-gray-100 rounded-md border border-dotted border-gray-300 bg-white shadow-sm">
            <li className="flex items-center justify-between py-4 pr-5 pl-4 text-sm leading-6">
              <div className="flex w-0 flex-1 items-center">
                <Info className="ml-2 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                <div className="ml-4 flex min-w-0 flex-1 gap-2">
                  <span className="flex items-center truncate font-medium">Aucune demande monétaire</span>
                  <div className="ml-auto">
                    <Button variant="outline" size="sm" disabled={!canCreate} onClick={openNewDemand}>
                      Nouvelle demande
                    </Button>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        )}
        {monetaryDemands?.length ? (
          <div className="mt-5 text-left">
            <Button variant="outline" size="sm" disabled={!canCreate} onClick={openNewDemand}>
              Nouvelle demande
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
